import * as React from 'react'
import { useRef, useState } from 'react'
import { DndProvider } from 'react-dnd-multi-backend'

import {
  Box,
  Hint,
  InfiniteScroll,
  Spinner,
  useClassNames,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { PlanningBalancesByPeriodsQuery, PlanningBalancesQuery } from 'generated/resources/graphql'
import { HTML5toTouch } from 'rdndmb-html5-to-touch'
import { useLocalStorage } from 'usehooks-ts'

import { observer } from 'mobx-react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import type { ShiftsRow, View } from 'modules/shift_management/lib/types'

import AddEmployeeRow from './AddEmployeeRow'
import CoverageSection from './CoverageSection/index'
import { CoverageSectionOld } from './CoverageSection/indexOld'
import EmployeesByDayRow from './EmployeesByDayRow'
import EmployeesByHourRow from './EmployeesByHourRow'
import Header from './Header'
import { ResizeOld } from './Header/ResizeOld'
import TimeOffRow from './TimeOffRow'
import VirtualList from './VirtualList'
import * as stylesRefs from './index.vanilla.css'
import { buildColumns, gridTemplateColumns } from './utils/buildColumns'

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]
type TimePlanningBalancesByPeriod = NonNullable<
  PlanningBalancesByPeriodsQuery['timePlanning']['balancesByPeriodsConnection']['nodes']
>[number]

// Number of elements preloaded before and after the visible area
const BASIC_VERSION_OFFSET = 5 //  For monthly, 4-weeks
const FULL_VERSION_OFFSET = 10 // For weekly, daily

interface Props {
  view: View
  shiftsRows: ShiftsRow[]
  showCoverageSection: boolean
  balancesByEmployee: Record<string, TimePlanningBalance>
  periodBalancesByEmployee: Record<string, TimePlanningBalancesByPeriod[]>
}

const DataTable = ({
  view,
  shiftsRows,
  showCoverageSection,
  balancesByEmployee,
  periodBalancesByEmployee,
}: Props) => {
  // remove this resizeHeight state when we remove the old coverage section
  const [resizeHeight, setResizeHeight] = useState<number>(140)
  const [hover, setHover] = useState<boolean>(false)
  const resizeRef = useRef<HTMLDivElement>(null)
  const cx = useClassNames(stylesRefs)
  const [{ page, hasMore, isLoading }, dispatchPagination] = usePagination()
  const [{ canUseCoverageView, hasFeatureFlag }] = useEnabledFeatures()
  const [{ canManageShifts, canManageUnassignedShifts }] = usePermissions()
  const { access } = useCurrentSession()

  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const getHoursToAdd = useUnsafeCallback((): number => {
    if (!view.isDaily) return 0

    return Math.max(...shiftsRows.map((row) => row.hoursInTheNextPeriod(view.lastDay)))
  }, [shiftsRows, view.isDaily, view.lastDay])
  const timeFormat = newEntryFlowEnabled ? access?.timeFormat : 24
  const hoursToAdd = useUnsafeMemo(() => getHoursToAdd(), [getHoursToAdd])
  const columns = useUnsafeMemo(
    () => buildColumns(view, hoursToAdd, timeFormat),
    [view, hoursToAdd, timeFormat]
  )

  const firstCellWidth = columns[0]?.minWidth || 's240'

  const loadMore = () => {
    if (isLoading) return

    dispatchPagination({ type: 'NEXT_PAGE' })
  }

  const showTimeOffRow = view.isByAreas
  const showEmployeesByHourRow = view.isByEmployees && view.isDaily
  const showEmployeesByDayRow = view.isByEmployees && !view.isDaily
  const showAddEmployeesRow = view.isByEmployees && canManageShifts
  const scrollRef = React.useRef<HTMLDivElement>(null)

  const canViewCoverageSection =
    canUseCoverageView && canManageUnassignedShifts && view.isByEmployees
  const [hasSeenNewEntryFlowHints] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHasSeenNewEntryFlowHints,
    false
  )

  return (
    <DndProvider options={HTML5toTouch} context={window}>
      <Box overflowX="auto" overflowY="overlay" ref={scrollRef} height="full-view-height">
        <Box className={cx('sticky')}>
          {!newEntryFlowEnabled &&
            canViewCoverageSection &&
            view.isWeekly &&
            showCoverageSection && (
              <Box className={cx('gridHeader')} ref={resizeRef}>
                <CoverageSectionOld columns={columns} view={view} resizeHeight={resizeHeight} />
              </Box>
            )}
          <Box
            className={cx('gridHeader')}
            style={{
              ...gridTemplateColumns(firstCellWidth, view, 'header', hoursToAdd),
            }}
            position="relative"
          >
            {!newEntryFlowEnabled && (
              <ResizeOld
                setResizeHeight={setResizeHeight}
                resizeRef={resizeRef}
                resize={showCoverageSection}
              />
            )}
            <Header shiftsRows={shiftsRows} columns={columns} key="header-row" />
            {showTimeOffRow && <TimeOffRow columns={columns} />}
            {showEmployeesByHourRow && !newEntryFlowEnabled && (
              <EmployeesByHourRow columns={columns} />
            )}
            {!newEntryFlowEnabled && showEmployeesByDayRow && (
              <EmployeesByDayRow columns={columns} />
            )}
          </Box>
        </Box>
        <InfiniteScroll
          threshold={800}
          pageStart={page}
          loadMore={loadMore}
          hasMore={hasMore}
          useWindow={false}
          getScrollParent={() => scrollRef.current}
          style={{
            minWidth: 'min-content',
            height: '100%',
            zIndex: 0,
            cursor: hover ? 'row-resize' : 'default',
            position: 'relative',
            display: newEntryFlowEnabled ? 'table' : '',
          }}
          loader={
            <Box
              alignItems="center"
              width="full"
              padding="s32"
              className={cx('loader')}
              style={{ gridColumn: `1 / ${columns.length + 1}` }}
              key="loading"
              flexGrow
            >
              <Spinner />
            </Box>
          }
        >
          <VirtualList
            shiftsRows={shiftsRows}
            balancesByEmployee={balancesByEmployee}
            periodBalancesByEmployee={periodBalancesByEmployee}
            itemHeight={60}
            columns={columns}
            scrollRef={scrollRef}
            preloadedElementsOutsideViewport={
              view.isFullVersion ? FULL_VERSION_OFFSET : BASIC_VERSION_OFFSET
            }
          />
          {showAddEmployeesRow && <AddEmployeeRow />}
        </InfiniteScroll>
        {newEntryFlowEnabled && canViewCoverageSection && (
          <Box
            justifyContent="flexEnd"
            className={cx('sticky')}
            width="full"
            style={{ bottom: '0', position: shiftsRows.length === 0 ? 'fixed' : '' }}
          >
            <Box className={`${cx('gridHeader')} bg-f1-background`} ref={resizeRef}>
              {!hasSeenNewEntryFlowHints ? (
                <Hint.Popover
                  step={3}
                  title={i18n.t('shift_management.hints.coverage.title')}
                  description={i18n.t('shift_management.hints.coverage.description')}
                  actionLabel={i18n.t('common.got_it')}
                  position="top left"
                >
                  <CoverageSection
                    columns={columns}
                    view={view}
                    hover={hover}
                    setHover={setHover}
                    resizeRef={resizeRef}
                  />
                </Hint.Popover>
              ) : (
                <CoverageSection
                  columns={columns}
                  view={view}
                  hover={hover}
                  setHover={setHover}
                  resizeRef={resizeRef}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </DndProvider>
  )
}

export default observer(DataTable)
